import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowRightArrowLeft,
  faBarcode,
  faBraille,
  faCircleHalfStroke,
  faCircleInfo,
  faCodeCompare,
  faCropSimple,
  faCrosshairs,
  faExpand,
  faEye,
  faEyeDropper,
  faFileArrowDown,
  faFileImage,
  faFilePdf,
  faFillDrip,
  faFont,
  faGrip,
  faGripLinesVertical,
  faHighlighter,
  faLayerGroup,
  faListCheck,
  faLocationCrosshairs,
  faLocationDot,
  faMagnifyingGlass,
  faMagnifyingGlassPlus,
  faNotEqual,
  faObjectGroup,
  faQrcode,
  faRightLeft,
  faRotate,
  faRulerCombined,
  faRulerHorizontal,
  faShareFromSquare,
  faSliders,
  faSwatchbook,
  faTable,
  faTableColumns,
  faTextHeight,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { faComment, faCopy, faHand, faSquare } from "@fortawesome/free-regular-svg-icons";

import { KpiCard } from "@/components/insights/KpiCard";
import { api, type PlanName, type UsagePanel, type UsagePanelParams, type UsageSurface } from "@/lib/api";
import {
  HEAT_DARK,
  HEAT_LIGHT,
  INTERNAL_SURFACES,
  PCT_THRESHOLDS,
  PLAN_LABELS,
  SURFACE_LABELS,
  USAGE_GROUP_LABELS,
  clickThresholds,
  dayCell,
  dayColumns,
  heatBin,
  heatInk,
  openingsByScope,
  openingsThatUsedSomething,
  orgCell,
  orgColumns,
  usageBreakdown,
  usageMinPlan,
  usageRows,
  type UsageRow,
} from "@/lib/usage";

const ICONES: Record<string, IconDefinition> = {
  mao: faHand,
  zoom: faMagnifyingGlass,
  rotacionar: faRotate,
  espelhar: faArrowRightArrowLeft,
  ajustar: faExpand,
  regua: faRulerHorizontal,
  area: faRulerCombined,
  ler_codigo: faBarcode,
  ler_braile: faBraille,
  ler_texto: faFont,
  ler_tabela: faTable,
  modo_texto: faHighlighter,
  comparar_texto: faCodeCompare,
  medir_texto: faTextHeight,
  reticula: faGrip,
  conta_gotas: faEyeDropper,
  comentarios: faComment,
  nota_ponto: faLocationDot,
  nota_selecao: faCropSimple,
  tema: faCircleHalfStroke,
  exportar_jpeg: faFileImage,
  paginas: faCopy,
  canais: faSwatchbook,
  camadas: faLayerGroup,
  cobertura: faFillDrip,
  painel_codigos: faQrcode,
  informacoes: faCircleInfo,
  baixar: faFileArrowDown,
  relatorio: faFilePdf,
  lado: faTableColumns,
  cortina: faGripLinesVertical,
  piscar: faEye,
  delta: faNotEqual,
  alinhar_auto: faWandMagicSparkles,
  por_objeto: faObjectGroup,
  referencia: faLocationCrosshairs,
  diferencas: faListCheck,
  identificar: faMagnifyingGlassPlus,
  foco: faCrosshairs,
  molduras: faSquare,
  perfil: faSliders,
  exportar_vis: faShareFromSquare,
  trocar_ab: faRightLeft,
};

/** A barra do viewer e a da comparação, na ordem em que os botões aparecem. */
const BARRAS: Record<"viewer" | "compare", { grupos: string[][]; painel: { titulo: string; chaves: string[] } }> = {
  viewer: {
    grupos: [
      ["mao", "rotacionar", "espelhar", "ajustar", "zoom"],
      ["regua", "area"],
      ["ler_codigo", "ler_braile", "ler_texto", "ler_tabela", "modo_texto", "comparar_texto", "medir_texto", "reticula", "conta_gotas"],
      ["comentarios", "nota_ponto", "nota_selecao"],
      ["exportar_jpeg", "tema"],
    ],
    painel: {
      titulo: "Painéis",
      chaves: ["canais", "camadas", "cobertura", "painel_codigos", "paginas", "informacoes", "baixar", "relatorio", "trocar_ab"],
    },
  },
  compare: {
    grupos: [
      ["lado", "cortina", "piscar", "delta", "trocar_ab"],
      ["alinhar_auto", "por_objeto", "referencia"],
      ["diferencas", "identificar", "foco", "molduras"],
      ["comparar_texto", "ler_codigo", "ler_braile", "ler_texto", "conta_gotas", "medir_texto"],
    ],
    painel: { titulo: "Só no gerenciador", chaves: ["perfil", "exportar_vis"] },
  },
};

/** O que só existe no gerenciador: sai da barra quando o uso interno está fora. */
const SO_GERENCIADOR = new Set(["reticula", "exportar_jpeg", "perfil", "exportar_vis"]);

const CONTROLE =
  "rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500";
const BOTAO =
  "rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40";

const inteiro = new Intl.NumberFormat("pt-BR");
const umaCasa = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (v: number) => `${umaCasa.format(v * 100)} %`;
const pctCurto = (v: number) => (v <= 0 ? "0%" : v < 0.1 ? `${umaCasa.format(v * 100)}%` : `${Math.round(v * 100)}%`);

type Aba = "ranking" | "dia" | "org" | "barra";
type Medida = "pct" | "cliques";
type Ordem = "pct" | "cliques" | "por" | "travado" | "nome";

interface Filtros {
  dias: number;
  onde: UsageSurface | "";
  plano: PlanName | "sessao" | "";
  /** `null` = todas; `""` = sem organização. */
  org: string | null;
  tipo: "packaging" | "document" | "";
  /**
   * O uso interno (22/10/2026, pedido do dono): o gerenciador, o link exportado
   * e quem abre com e-mail @linkflow.com.br. Desligado por padrão.
   */
  interno: boolean;
}

const FILTROS_PADRAO: Filtros = { dias: 30, onde: "", plano: "", org: null, tipo: "", interno: false };

function PlanTag({ plan }: { plan: PlanName | null }) {
  if (!plan) return null;
  const cor =
    plan === "enterprise"
      ? "border-violet-800 bg-violet-950 text-violet-300"
      : plan === "pro"
        ? "border-amber-800 bg-amber-950 text-amber-300"
        : "border-ink-600 bg-ink-800 text-ink-300";
  return (
    <span className={`inline-block rounded border px-1 text-[9px] font-bold uppercase tracking-wide ${cor}`}>{plan}</span>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const largura = 84;
  const altura = 22;
  const margem = 2.5;
  if (!values.length) return null;
  const maximo = Math.max(...values, 1e-9);
  const x = (i: number) => margem + ((largura - 2 * margem) * (values.length <= 1 ? 0 : i / (values.length - 1)));
  const y = (v: number) => altura - margem - (altura - 2 * margem) * (v / maximo);
  const pontos = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const ultimo = values.length - 1;
  return (
    <svg viewBox={`0 0 ${largura} ${altura}`} width={largura} height={altura} aria-hidden="true">
      <polygon points={`${x(0).toFixed(1)},${altura - margem} ${pontos} ${x(ultimo).toFixed(1)},${altura - margem}`} fill="#5598e726" />
      <polyline points={pontos} fill="none" stroke="#5598e7" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(ultimo)} cy={y(values[ultimo])} r="2.2" fill="#5598e7" />
    </svg>
  );
}

function Legenda({ limites, emPct, tema = "dark", extra }: { limites: readonly number[]; emPct: boolean; tema?: "dark" | "light"; extra?: ReactNode }) {
  const paleta = tema === "dark" ? HEAT_DARK : HEAT_LIGHT;
  const fmt = (v: number) => (emPct ? `${Math.round(v * 100)} %` : inteiro.format(Math.round(v)));
  const rotulos = ["0", `< ${fmt(limites[0])}`];
  for (let i = 0; i < limites.length - 1; i += 1) rotulos.push(`${fmt(limites[i])}–${fmt(limites[i + 1])}`);
  rotulos.push(`≥ ${fmt(limites[limites.length - 1])}`);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-400">
      <span>{emPct ? "Aberturas que usaram:" : "Cliques:"}</span>
      {rotulos.map((rotulo, i) => (
        <span key={rotulo} className="inline-flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block h-2.5 w-5 rounded-sm border border-ink-700" style={{ background: paleta[i] }} />
          {rotulo}
        </span>
      ))}
      {extra}
    </div>
  );
}

function OndeExiste({ row }: { row: UsageRow }) {
  if (row.def.sessionOnly) return <span className="rounded-full border border-ink-700 px-1.5 text-[10px] text-ink-300">Só gerenciador</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {row.def.scopes.includes("viewer") ? <span className="rounded-full border border-ink-700 px-1.5 text-[10px] text-ink-300">Viewer</span> : null}
      {row.def.scopes.includes("compare") ? <span className="rounded-full border border-ink-700 px-1.5 text-[10px] text-ink-300">Comparação</span> : null}
    </span>
  );
}

function NomeDoRecurso({ row }: { row: UsageRow }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded bg-ink-800 text-ink-300">
        <FontAwesomeIcon icon={ICONES[row.key] ?? faCircleInfo} className="h-3 w-3" />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-medium text-ink-100">{row.def.label}</span>
        <span className="block truncate text-[11px] text-ink-500">{USAGE_GROUP_LABELS[row.def.group]}</span>
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// As quatro abas
// ---------------------------------------------------------------------------

function Ranking({ panel, rows, onOpen }: { panel: UsagePanel; rows: UsageRow[]; onOpen: (key: string) => void }) {
  const [ordem, setOrdem] = useState<Ordem>("pct");
  const ordenadas = useMemo(() => {
    const por = (r: UsageRow) => (r.used > 0 ? r.clicks / r.used : 0);
    const comparar: Record<Ordem, (a: UsageRow, b: UsageRow) => number> = {
      pct: (a, b) => b.pct - a.pct,
      cliques: (a, b) => b.clicks - a.clicks,
      por: (a, b) => por(b) - por(a),
      travado: (a, b) => b.lockedClicks - a.lockedClicks || b.pct - a.pct,
      nome: (a, b) => a.def.label.localeCompare(b.def.label, "pt-BR"),
    };
    return [...rows].sort(comparar[ordem]);
  }, [ordem, rows]);
  const maximo = Math.max(...rows.map((r) => r.pct), 1e-9);
  const colunas = "1.6rem minmax(12rem,1.7fr) 8.5rem minmax(11rem,1.5fr) 5.5rem 5.4rem 7.6rem 6.2rem";
  const cabecalho = (chave: Ordem, rotulo: string, direita = false) => (
    <span className={direita ? "text-right" : undefined}>
      <button
        type="button"
        onClick={() => setOrdem(chave)}
        aria-pressed={ordem === chave}
        className={`uppercase tracking-wide ${ordem === chave ? "text-ink-100" : "hover:text-ink-200"}`}
      >
        {rotulo}
        {ordem === chave ? " ↓" : ""}
      </button>
    </span>
  );
  return (
    <div className="flex min-h-[12rem] flex-col overflow-hidden rounded-lg border border-ink-700">
      <div className="overflow-x-auto">
        <div className="min-w-[56rem]">
          <div
            className="grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-[11px] font-medium text-ink-400"
            style={{ gridTemplateColumns: colunas }}
          >
            <span>#</span>
            {cabecalho("nome", "Recurso")}
            <span className="uppercase tracking-wide">Onde existe</span>
            {cabecalho("pct", "Aberturas que usaram")}
            {cabecalho("cliques", "Cliques", true)}
            {cabecalho("por", "Por abertura", true)}
            {cabecalho("travado", "No travado", true)}
            <span className="uppercase tracking-wide">Tendência</span>
          </div>
          {ordenadas.map((row, i) => (
            <button
              key={row.key}
              type="button"
              onClick={() => onOpen(row.key)}
              className="grid w-full items-center gap-x-3 border-b border-ink-800 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-ink-850"
              style={{ gridTemplateColumns: colunas }}
            >
              <span className="tabular-nums text-ink-500">{i + 1}</span>
              <NomeDoRecurso row={row} />
              <OndeExiste row={row} />
              <span className="flex min-w-0 items-center gap-2">
                <span className="relative h-2 flex-1 overflow-hidden rounded bg-ink-800">
                  <span className="absolute inset-y-0 left-0 rounded-r bg-[#2f7ad6]" style={{ width: `${((row.pct / maximo) * 100).toFixed(1)}%` }} />
                </span>
                <span className="w-16 text-right font-semibold tabular-nums text-ink-100">{pct(row.pct)}</span>
              </span>
              <span className="text-right tabular-nums text-ink-200">{inteiro.format(row.clicks)}</span>
              <span className="text-right tabular-nums text-ink-200">{row.used > 0 ? umaCasa.format(row.clicks / row.used) : "—"}</span>
              <span className="flex items-center justify-end gap-1 tabular-nums text-ink-200">
                {row.lockedClicks > 0 ? (
                  <>
                    {inteiro.format(row.lockedClicks)} <PlanTag plan={usageMinPlan(panel, row.key)} />
                  </>
                ) : (
                  <span className="text-ink-600">—</span>
                )}
              </span>
              <Sparkline values={row.daily.slice(-30)} />
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
        {rows.length} recursos · clique numa linha para ver por onde, por plano e por organização · «No travado» são cliques de quem viu o botão com o selo do plano de cima
      </div>
    </div>
  );
}

function PorDia({ panel, rows, medida, onOpen }: { panel: UsagePanel; rows: UsageRow[]; medida: Medida; onOpen: (key: string) => void }) {
  const colunas = useMemo(() => dayColumns(panel.days), [panel.days]);
  const linhas = useMemo(
    () =>
      rows
        .map((row) => ({ row, celulas: colunas.map((coluna) => dayCell(panel, row, coluna)) }))
        .sort((a, b) => (medida === "pct" ? b.row.pct - a.row.pct : b.row.clicks - a.row.clicks)),
    [colunas, medida, panel, rows],
  );
  const limites = medida === "pct" ? PCT_THRESHOLDS : clickThresholds(Math.max(1, ...linhas.flatMap((l) => l.celulas.map((c) => c.clicks))));
  const largura = colunas.length <= 8 ? "minmax(44px,1fr)" : colunas.length <= 31 ? "minmax(17px,1fr)" : "minmax(40px,1fr)";
  return (
    <div className="space-y-2">
      <Legenda limites={limites} emPct={medida === "pct"} />
      <div className="overflow-x-auto rounded-lg border border-ink-700 bg-ink-850">
        <div className="grid w-max min-w-full gap-[2px] p-3" style={{ gridTemplateColumns: `12.5rem repeat(${colunas.length}, ${largura}) 4.6rem` }}>
          <span className="self-end pb-1 text-[10px] text-ink-500">Recurso</span>
          {colunas.map((coluna) => (
            <span key={coluna.title} title={coluna.title} className={`self-end pb-1 text-center text-[9px] tabular-nums ${coluna.weekend ? "text-ink-600" : "text-ink-400"}`}>
              {coluna.label}
            </span>
          ))}
          <span className="self-end pb-1 text-right text-[10px] text-ink-500">{medida === "pct" ? "Período" : "Total"}</span>
          {linhas.map(({ row, celulas }) => (
            <DiaLinha key={row.key} row={row} celulas={celulas} colunas={colunas} limites={limites} medida={medida} onOpen={onOpen} />
          ))}
        </div>
      </div>
      <p className="text-[11px] text-ink-500">
        {colunas.length > 31 ? "Em 90 dias cada coluna é uma semana. " : ""}
        Em «% das aberturas» o fim de semana não desbota, porque a base também cai; em «Cliques» aparece o volume.
      </p>
    </div>
  );
}

function DiaLinha({
  row,
  celulas,
  colunas,
  limites,
  medida,
  onOpen,
}: {
  row: UsageRow;
  celulas: ReturnType<typeof dayCell>[];
  colunas: ReturnType<typeof dayColumns>;
  limites: readonly number[];
  medida: Medida;
  onOpen: (key: string) => void;
}) {
  return (
    <>
      <button type="button" onClick={() => onOpen(row.key)} className="flex min-w-0 items-center gap-1.5 pr-2 text-left text-[11px] text-ink-200 hover:text-ink-50">
        <FontAwesomeIcon icon={ICONES[row.key] ?? faCircleInfo} className="h-2.5 w-2.5 shrink-0 text-ink-400" />
        <span className="truncate">{row.def.label}</span>
      </button>
      {celulas.map((celula, i) => {
        const faixa = heatBin(medida === "pct" ? celula.pct : celula.clicks, limites);
        return (
          <button
            key={colunas[i].title}
            type="button"
            onClick={() => onOpen(row.key)}
            title={`${row.def.label} · ${colunas[i].title}\n${pct(celula.pct)} das aberturas (${inteiro.format(celula.used)} de ${inteiro.format(celula.base)})\n${inteiro.format(celula.clicks)} cliques`}
            className="h-[18px] rounded-sm hover:outline hover:outline-1 hover:outline-ink-100"
            style={{ background: HEAT_DARK[faixa] }}
          />
        );
      })}
      <span className="whitespace-nowrap pl-2 text-right text-[11px] tabular-nums text-ink-300">
        {medida === "pct" ? pct(row.pct) : inteiro.format(row.clicks)}
      </span>
    </>
  );
}

function PorOrganizacao({
  panel,
  rows,
  medida,
  selecionada,
  onOpen,
}: {
  panel: UsagePanel;
  rows: UsageRow[];
  medida: Medida;
  selecionada: string | null;
  onOpen: (key: string) => void;
}) {
  const { columns, hidden } = useMemo(() => orgColumns(panel), [panel]);
  const linhas = useMemo(
    () =>
      rows
        .map((row) => ({ row, celulas: columns.map((coluna) => orgCell(panel, row, coluna)) }))
        .sort((a, b) => (medida === "pct" ? b.row.pct - a.row.pct : b.row.clicks - a.row.clicks)),
    [columns, medida, panel, rows],
  );
  if (!columns.length) return <p className="py-10 text-center text-sm text-ink-500">Nenhuma abertura com esse filtro.</p>;
  const limites =
    medida === "pct"
      ? PCT_THRESHOLDS
      : clickThresholds(Math.max(1, ...linhas.flatMap((l) => l.celulas.map((c) => (c.kind === "value" ? c.clicks : 0)))));
  const marcada = (id: string) => selecionada !== null && (selecionada === id || (selecionada === "" && id === ""));
  return (
    <div className="space-y-2">
      <Legenda
        limites={limites}
        emPct={medida === "pct"}
        extra={
          <>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2.5 w-5 rounded-sm" style={{ background: "repeating-linear-gradient(135deg,#283142 0 3px,#141a28 3px 7px)" }} />
              travado pelo plano
            </span>
            <span>— não existe ali</span>
          </>
        }
      />
      <div className="overflow-x-auto rounded-lg border border-ink-700 bg-ink-850">
        <div className="grid w-max min-w-full gap-[2px] p-3" style={{ gridTemplateColumns: `12.5rem repeat(${columns.length}, minmax(5.4rem,1fr))` }}>
          <span />
          {columns.map((coluna) => (
            <span key={coluna.id || "sem"} className={`flex flex-col items-center gap-0.5 px-0.5 pb-1.5 text-center ${marcada(coluna.id) ? "text-ink-50" : "text-ink-200"}`}>
              <span className="line-clamp-2 text-[11px] font-semibold leading-tight" title={coluna.name}>
                {coluna.name}
              </span>
              <span className="flex items-center gap-1 text-[10px] text-ink-500">
                {coluna.team ? <PlanTag plan={null} /> : <PlanTag plan={coluna.topPlan} />}
                {inteiro.format(coluna.openings.viewer + coluna.openings.compare)}
              </span>
            </span>
          ))}
          {linhas.map(({ row, celulas }) => (
            <OrgLinha key={row.key} row={row} celulas={celulas} columns={columns} limites={limites} medida={medida} marcada={marcada} onOpen={onOpen} />
          ))}
        </div>
      </div>
      <p className="text-[11px] text-ink-500">
        Esta aba sempre mostra as organizações do período; a escolhida no filtro fica contornada. «Sem organização» são links do LinkFlow que chegaram sem o cabeçalho da empresa — não é o gerenciador.
        {hidden ? ` Mostrando as ${columns.filter((c) => c.id && !c.team).length} com mais aberturas (${hidden} fora).` : ""}
      </p>
    </div>
  );
}

function OrgLinha({
  row,
  celulas,
  columns,
  limites,
  medida,
  marcada,
  onOpen,
}: {
  row: UsageRow;
  celulas: ReturnType<typeof orgCell>[];
  columns: ReturnType<typeof orgColumns>["columns"];
  limites: readonly number[];
  medida: Medida;
  marcada: (id: string) => boolean;
  onOpen: (key: string) => void;
}) {
  return (
    <>
      <button type="button" onClick={() => onOpen(row.key)} className="flex min-w-0 items-center gap-1.5 pr-2 text-left text-[11px] text-ink-200 hover:text-ink-50">
        <FontAwesomeIcon icon={ICONES[row.key] ?? faCircleInfo} className="h-2.5 w-2.5 shrink-0 text-ink-400" />
        <span className="truncate">{row.def.label}</span>
      </button>
      {celulas.map((celula, i) => {
        const coluna = columns[i];
        const contorno = marcada(coluna.id) ? " ring-1 ring-inset ring-ink-100" : "";
        if (celula.kind === "absent") {
          return (
            <span key={coluna.id || "sem"} title={`${row.def.label} · ${coluna.name}\nNão existe aqui`} className={`grid h-7 place-items-center text-[11px] text-ink-600${contorno}`}>
              —
            </span>
          );
        }
        if (celula.kind === "locked") {
          return (
            <button
              key={coluna.id || "sem"}
              type="button"
              onClick={() => onOpen(row.key)}
              title={`${row.def.label} · ${coluna.name}\nTravado no plano ${coluna.topPlan ? PLAN_LABELS[coluna.topPlan] : "do link"} (é do ${PLAN_LABELS[celula.requiredPlan]})\n${inteiro.format(celula.lockedClicks)} cliques no botão travado`}
              className={`grid h-7 place-items-center rounded-sm${contorno}`}
              style={{ background: "repeating-linear-gradient(135deg,#283142 0 3px,#141a28 3px 7px)" }}
            >
              <PlanTag plan={celula.requiredPlan} />
            </button>
          );
        }
        const faixa = heatBin(medida === "pct" ? celula.pct : celula.clicks, limites);
        return (
          <button
            key={coluna.id || "sem"}
            type="button"
            onClick={() => onOpen(row.key)}
            title={`${row.def.label} · ${coluna.name}\n${pct(celula.pct)} das aberturas (${inteiro.format(celula.used)} de ${inteiro.format(celula.base)})\n${inteiro.format(celula.clicks)} cliques`}
            className={`grid h-7 place-items-center rounded-sm text-[11px] font-semibold tabular-nums${contorno}`}
            style={{ background: HEAT_DARK[faixa], color: heatInk(faixa, "dark") }}
          >
            {medida === "pct" ? pctCurto(celula.pct) : inteiro.format(celula.clicks)}
          </button>
        );
      })}
    </>
  );
}

function NaBarra({
  panel,
  rows,
  medida,
  interno,
  onOpen,
}: {
  panel: UsagePanel;
  rows: UsageRow[];
  medida: Medida;
  interno: boolean;
  onOpen: (key: string) => void;
}) {
  const [barra, setBarra] = useState<"viewer" | "compare">("viewer");
  const [tema, setTema] = useState<"light" | "dark">("light");
  const porChave = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);
  const aberturas = openingsByScope(panel);
  const valor = (chave: string) => {
    const row = porChave.get(chave);
    if (!row) return null;
    if (medida === "cliques") return { v: row.clicks, texto: inteiro.format(row.clicks), row };
    // Na barra da comparação, o % é sobre as aberturas de comparação (e vice-versa).
    const feature = panel.features.find((item) => item.feature === chave);
    const usaram = (feature?.days[barra]?.openings ?? []).reduce((a, b) => a + b, 0);
    const base = aberturas[barra];
    const fracao = base > 0 ? usaram / base : 0;
    return { v: fracao, texto: pctCurto(fracao), row };
  };
  // Sem o uso interno, o que só existe no gerenciador sai da barra.
  const existe = (chave: string) => interno || porChave.has(chave) || !SO_GERENCIADOR.has(chave);
  const grupos = BARRAS[barra].grupos.map((grupo) => grupo.filter(existe)).filter((grupo) => grupo.length);
  const painel = { ...BARRAS[barra].painel, chaves: BARRAS[barra].painel.chaves.filter(existe) };
  const chaves = [...grupos.flat(), ...painel.chaves];
  const limites = medida === "pct" ? PCT_THRESHOLDS : clickThresholds(Math.max(1, ...chaves.map((c) => valor(c)?.v ?? 0)));
  const paleta = tema === "light" ? HEAT_LIGHT : HEAT_DARK;
  const superficie = tema === "light" ? "#ffffff" : "#1a1d23";
  const borda = tema === "light" ? "#e4e7ec" : "#2d323c";
  const apagado = tema === "light" ? "#667085" : "#8b949e";
  const botao = (chave: string) => {
    const v = valor(chave);
    const rotulo = porChave.get(chave)?.def.label ?? chave;
    if (!v) {
      return (
        <span key={chave} title={`${rotulo}\nNão existe com este filtro`} className="flex h-[58px] min-w-[3.5rem] flex-col items-center justify-center gap-1 rounded-md px-2 text-[10px] font-semibold opacity-50" style={{ color: apagado }}>
          <FontAwesomeIcon icon={ICONES[chave] ?? faCircleInfo} className="h-[15px] w-[15px]" />
          <span className="whitespace-nowrap">{rotulo}</span>
          <span>—</span>
        </span>
      );
    }
    const faixa = heatBin(v.v, limites);
    const travado = v.row.lockedClicks > 0 ? `\n${inteiro.format(v.row.lockedClicks)} cliques no botão travado` : "";
    return (
      <button
        key={chave}
        type="button"
        onClick={() => onOpen(chave)}
        title={`${rotulo}\n${medida === "pct" ? `${v.texto} das aberturas` : `${v.texto} cliques`}${travado}`}
        className="flex h-[58px] min-w-[3.5rem] flex-col items-center justify-center gap-1 rounded-md px-2 text-[10px] font-semibold hover:outline hover:outline-2 hover:outline-[#3b82f6]"
        style={{ background: paleta[faixa], color: heatInk(faixa, tema) }}
      >
        <FontAwesomeIcon icon={ICONES[chave] ?? faCircleInfo} className="h-[15px] w-[15px]" />
        <span className="whitespace-nowrap">{rotulo}</span>
        <span className="tabular-nums">{v.texto}</span>
      </button>
    );
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Segmentado
          valor={barra}
          opcoes={[
            ["viewer", "Barra do viewer"],
            ["compare", "Barra da comparação"],
          ]}
          onChange={(v) => setBarra(v as "viewer" | "compare")}
        />
        <Segmentado
          valor={tema}
          opcoes={[
            ["light", "Viewer claro"],
            ["dark", "Viewer escuro"],
          ]}
          onChange={(v) => setTema(v as "light" | "dark")}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-ink-700" style={{ background: superficie }}>
        <div className="overflow-x-auto border-b px-4 py-2" style={{ borderColor: borda }}>
          <div className="flex w-max items-center gap-2">
            {grupos.map((grupo, i) => (
              <span key={grupo.join()} className="flex items-center gap-2">
                {i ? <span className="h-8 w-px" style={{ background: borda }} /> : null}
                <span className="flex items-center gap-0.5">{grupo.map(botao)}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-3">
          <span className="w-full text-[10px] font-semibold uppercase tracking-wide" style={{ color: apagado }}>
            {painel.titulo}
          </span>
          {painel.chaves.length ? painel.chaves.map(botao) : <span className="text-[11px]" style={{ color: apagado }}>Só existe no uso interno.</span>}
        </div>
      </div>
      <Legenda limites={limites} emPct={medida === "pct"} tema={tema} />
      <p className="text-[11px] text-ink-500">
        A barra do viewer com cada botão pintado pelo uso no filtro. {barra === "compare" ? "Na comparação, o % é sobre as aberturas de comparação." : "No viewer, o % é sobre as aberturas de viewer."}
      </p>
    </div>
  );
}

function Segmentado({ valor, opcoes, onChange }: { valor: string; opcoes: [string, string][]; onChange: (valor: string) => void }) {
  return (
    <span className="inline-flex gap-0.5 rounded-md border border-ink-700 bg-ink-850 p-0.5" role="group">
      {opcoes.map(([v, rotulo]) => (
        <button
          key={v}
          type="button"
          aria-pressed={valor === v}
          onClick={() => onChange(v)}
          className={`rounded px-2.5 py-1 text-xs ${valor === v ? "bg-ink-700 text-ink-100" : "text-ink-400 hover:text-ink-200"}`}
        >
          {rotulo}
        </button>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// O detalhe de um recurso
// ---------------------------------------------------------------------------

function Detalhe({ panel, row, onClose }: { panel: UsagePanel; row: UsageRow; onClose: () => void }) {
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onClose();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [onClose]);
  const porOnde = usageBreakdown(panel, row, "surface");
  const porPlano = usageBreakdown(panel, row, "plan");
  const { columns } = orgColumns(panel, 500);
  const orgs = columns
    .filter((coluna) => coluna.id && !coluna.team)
    .map((coluna) => ({ coluna, celula: orgCell(panel, row, coluna) }));
  const usam = orgs
    .filter((o) => o.celula.kind === "value")
    .sort((a, b) => (b.celula.kind === "value" ? b.celula.pct : 0) - (a.celula.kind === "value" ? a.celula.pct : 0))
    .slice(0, 6);
  const travados = orgs.filter((o) => o.celula.kind === "locked" && o.celula.lockedClicks > 0);
  const minimo = usageMinPlan(panel, row.key);
  const barra = (rotulo: string, fracao: number, maximo: number, texto?: string) => (
    <div key={rotulo} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_4.4rem] items-center gap-2 py-0.5 text-xs text-ink-300">
      <span className="truncate" title={rotulo}>
        {rotulo}
      </span>
      <span className="relative h-2 overflow-hidden rounded bg-ink-800">
        <span className="absolute inset-y-0 left-0 rounded-r bg-[#2f7ad6]" style={{ width: `${maximo > 0 ? ((fracao / maximo) * 100).toFixed(1) : 0}%` }} />
      </span>
      <span className="text-right font-medium tabular-nums text-ink-100">{texto ?? pct(fracao)}</span>
    </div>
  );
  const maxOnde = Math.max(...porOnde.map((x) => x.pct), 1e-9);
  const maxPlano = Math.max(...porPlano.map((x) => x.pct), 1e-9);
  const maxUsam = Math.max(...usam.map((x) => (x.celula.kind === "value" ? x.celula.pct : 0)), 1e-9);
  const maxTravado = Math.max(...travados.map((x) => (x.celula.kind === "locked" ? x.celula.lockedClicks : 0)), 1e-9);
  return (
    <>
      <button type="button" aria-label="Fechar detalhe" onClick={onClose} className="fixed inset-0 z-40 bg-black/50" />
      <aside role="dialog" aria-modal="true" aria-label={`Detalhe de ${row.def.label}`} className="fixed bottom-3 right-3 top-3 z-50 flex w-[min(420px,calc(100vw-24px))] flex-col overflow-auto rounded-lg border border-ink-700 bg-ink-900 shadow-2xl">
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-ink-800 bg-ink-900 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-50">
              {row.def.label} <PlanTag plan={minimo} />
            </h2>
            <p className="mt-0.5 text-xs text-ink-400">
              {USAGE_GROUP_LABELS[row.def.group]} · {row.def.sessionOnly ? "só gerenciador" : row.def.scopes.length > 1 ? "viewer e comparação" : row.def.scopes[0] === "viewer" ? "viewer" : "comparação"}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-xl leading-none text-ink-400 hover:text-ink-100">
            ×
          </button>
        </div>
        <div className="flex flex-col gap-5 px-5 py-4">
          <div className="grid grid-cols-2 gap-2.5">
            <KpiCard label="Aberturas que usaram" value={pct(row.pct)} subLabel={`${inteiro.format(row.used)} de ${inteiro.format(row.base)}`} />
            <KpiCard label="Cliques" value={inteiro.format(row.clicks)} />
            <KpiCard label="Cliques por abertura" value={row.used > 0 ? umaCasa.format(row.clicks / row.used) : "—"} />
            <KpiCard label="Cliques no travado" value={row.lockedClicks > 0 ? inteiro.format(row.lockedClicks) : "—"} />
          </div>
          <section>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Por onde</h3>
            {porOnde.map((x) => barra(SURFACE_LABELS[x.key as UsageSurface] ?? x.key, x.pct, maxOnde))}
          </section>
          <section>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Por plano</h3>
            {porPlano.map((x) => barra(PLAN_LABELS[x.key as keyof typeof PLAN_LABELS] ?? x.key, x.pct, maxPlano))}
          </section>
          <section>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Organizações que mais usam</h3>
            {usam.length ? (
              usam.map((x) => barra(x.coluna.name, x.celula.kind === "value" ? x.celula.pct : 0, maxUsam))
            ) : (
              <p className="text-xs text-ink-500">Nenhuma organização usou no período.</p>
            )}
          </section>
          {travados.length ? (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Clicaram no botão travado</h3>
              {travados.map((x) =>
                barra(x.coluna.name, x.celula.kind === "locked" ? x.celula.lockedClicks : 0, maxTravado, inteiro.format(x.celula.kind === "locked" ? x.celula.lockedClicks : 0)),
              )}
              <p className="mt-1.5 text-[11px] text-ink-500">Quem viu o selo do plano de cima e clicou mesmo assim: interesse no upgrade.</p>
            </section>
          ) : null}
          <p className="text-[11px] text-ink-500">Conta quando a pessoa liga o recurso (clique ou atalho de teclado). Zoom e arrasto pela roda ou trackpad não contam.</p>
        </div>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// A página
// ---------------------------------------------------------------------------

function paraParametros(filtros: Filtros): UsagePanelParams {
  return {
    days: filtros.dias,
    surface: filtros.onde || undefined,
    plan: filtros.plano || undefined,
    organization_id: filtros.org ?? undefined,
    viewer_kind: filtros.tipo || undefined,
    include_internal: filtros.interno,
  };
}

function csvDe(rows: UsageRow[]): string {
  const aspas = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const linhas = [["Recurso", "Grupo", "Aberturas que usaram", "Aberturas", "%", "Cliques", "Cliques no travado"].map(aspas).join(";")];
  for (const row of rows) {
    linhas.push(
      [row.def.label, USAGE_GROUP_LABELS[row.def.group], String(row.used), String(row.base), umaCasa.format(row.pct * 100), String(row.clicks), String(row.lockedClicks)]
        .map(aspas)
        .join(";"),
    );
  }
  return linhas.join("\n");
}

/**
 * Uso dos recursos (21/10/2026): quais botões do viewer e da comparação são
 * usados, onde e por quem. Pedido do dono; as quatro abas são as quatro
 * maneiras de ver o mesmo número. A conta mora em `lib/usage.ts`; a contagem,
 * em `lib/usageTracker.ts` e `services/feature_usage.py`.
 */
export function Usage() {
  // `?org=` vem do atalho «uso» da aba «Organizações» da Auditoria.
  const [filtros, setFiltros] = useState<Filtros>(() => {
    const org = new URLSearchParams(window.location.search).get("org");
    return org === null ? FILTROS_PADRAO : { ...FILTROS_PADRAO, org };
  });
  const [aba, setAba] = useState<Aba>("ranking");
  const [medida, setMedida] = useState<Medida>("pct");
  const [painel, setPainel] = useState<UsagePanel | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [aberto, setAberto] = useState<string | null>(null);

  useEffect(() => {
    const controle = new AbortController();
    setCarregando(true);
    setErro(null);
    api
      .getUsagePanel(paraParametros(filtros), controle.signal)
      .then(setPainel)
      .catch((falha) => {
        if (!controle.signal.aborted) setErro(falha instanceof Error && falha.message ? falha.message : "Não foi possível carregar o uso.");
      })
      .finally(() => {
        if (!controle.signal.aborted) setCarregando(false);
      });
    return () => controle.abort();
  }, [filtros, recarga]);

  const linhas = useMemo(
    () => (painel ? usageRows(painel, filtros.onde, filtros.interno) : []),
    [filtros.interno, filtros.onde, painel],
  );
  const aberturas = painel ? openingsByScope(painel) : { viewer: 0, compare: 0 };
  const totalDeAberturas = aberturas.viewer + aberturas.compare;
  const usaramAlgo = painel ? openingsThatUsedSomething(painel) : 0;
  const recursosPorAbertura = totalDeAberturas > 0 ? linhas.reduce((acc, row) => acc + row.used, 0) / totalDeAberturas : 0;
  const maisTravado = linhas.reduce<UsageRow | null>((melhor, row) => (row.lockedClicks > (melhor?.lockedClicks ?? 0) ? row : melhor), null);
  const travadoTotal = linhas.reduce((acc, row) => acc + row.lockedClicks, 0);
  const filtrado = filtros.dias !== 30 || filtros.onde || filtros.plano || filtros.org !== null || filtros.tipo || filtros.interno;
  const linhaAberta = aberto ? linhas.find((row) => row.key === aberto) ?? null : null;

  const exportar = () => {
    const blob = new Blob(["﻿" + csvDe(linhas)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `uso-dos-recursos-${filtros.dias}d.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const definir = <K extends keyof Filtros>(chave: K, valor: Filtros[K]) => setFiltros((atual) => ({ ...atual, [chave]: valor }));
  // Desligar o uso interno leva junto o recorte que só existe nele.
  const alternarInterno = (ligado: boolean) =>
    setFiltros((atual) => ({
      ...atual,
      interno: ligado,
      onde: !ligado && INTERNAL_SURFACES.includes(atual.onde as UsageSurface) ? "" : atual.onde,
      plano: !ligado && atual.plano === "sessao" ? "" : atual.plano,
    }));
  const superficies = (Object.keys(SURFACE_LABELS) as UsageSurface[]).filter(
    (chave) => filtros.interno || !INTERNAL_SURFACES.includes(chave),
  );

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-ink-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-100">Uso dos recursos</h1>
          <p className="mt-0.5 text-xs text-ink-400">Quais recursos do viewer e da comparação são usados, onde e por quem.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportar} disabled={!linhas.length} className={BOTAO}>
            Exportar CSV
          </button>
          <button type="button" onClick={() => setRecarga((n) => n + 1)} disabled={carregando} className={BOTAO}>
            ↺ Atualizar
          </button>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-ink-400" htmlFor="uso-periodo">Período:</label>
          <select id="uso-periodo" className={CONTROLE} value={filtros.dias} onChange={(e) => definir("dias", Number(e.target.value))}>
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
          </select>
          <label className="text-xs font-medium text-ink-400" htmlFor="uso-onde">Onde:</label>
          <select id="uso-onde" className={CONTROLE} value={filtros.onde} onChange={(e) => definir("onde", e.target.value as Filtros["onde"])}>
            <option value="">Todos</option>
            {superficies.map((chave) => (
              <option key={chave} value={chave}>
                {SURFACE_LABELS[chave]}
              </option>
            ))}
          </select>
          <label className="text-xs font-medium text-ink-400" htmlFor="uso-plano">Plano:</label>
          <select id="uso-plano" className={CONTROLE} value={filtros.plano} onChange={(e) => definir("plano", e.target.value as Filtros["plano"])}>
            <option value="">Todos</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
            {filtros.interno ? <option value="sessao">Uso interno</option> : null}
          </select>
          <label className="text-xs font-medium text-ink-400" htmlFor="uso-org">Organização:</label>
          <select
            id="uso-org"
            className={`${CONTROLE} max-w-[14rem]`}
            value={filtros.org === null ? "__todas__" : filtros.org}
            onChange={(e) => definir("org", e.target.value === "__todas__" ? null : e.target.value)}
          >
            <option value="__todas__">Todas</option>
            {(painel?.organizations ?? []).map((org) => (
              <option key={org.id} value={org.id}>
                {org.name || org.id}
              </option>
            ))}
            <option value="">Sem organização</option>
          </select>
          <label className="text-xs font-medium text-ink-400" htmlFor="uso-tipo">Tipo:</label>
          <select id="uso-tipo" className={CONTROLE} value={filtros.tipo} onChange={(e) => definir("tipo", e.target.value as Filtros["tipo"])}>
            <option value="">Todos</option>
            <option value="packaging">Embalagem</option>
            <option value="document">Documento</option>
          </select>
          <label
            className="flex items-center gap-1.5 text-xs text-ink-300"
            title="O gerenciador, os links exportados pelo gerenciador e quem abre com e-mail @linkflow.com.br"
          >
            <input type="checkbox" checked={filtros.interno} onChange={(e) => alternarInterno(e.target.checked)} />
            Incluir uso interno
          </label>
          {filtrado ? (
            <button type="button" onClick={() => setFiltros(FILTROS_PADRAO)} className={`ml-auto ${BOTAO}`}>
              Limpar filtros
            </button>
          ) : null}
        </div>

        {erro ? (
          <p className="flex shrink-0 items-center justify-between rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            <span>{erro}</span>
            <button type="button" onClick={() => setRecarga((n) => n + 1)} className={BOTAO}>
              Tentar de novo
            </button>
          </p>
        ) : null}

        {!painel && carregando ? <p className="py-12 text-center text-sm text-ink-500">Carregando…</p> : null}

        {painel && totalDeAberturas === 0 ? (
          <div className="rounded-lg border border-dashed border-ink-700 px-6 py-12 text-center text-sm text-ink-500">
            Nenhuma abertura com esse filtro. A contagem começa no dia em que esta versão entrou no ar — não há histórico de antes.
          </div>
        ) : null}

        {painel && totalDeAberturas > 0 ? (
          <>
            <div className="grid shrink-0 grid-cols-2 gap-4 lg:grid-cols-4">
              <KpiCard
                label="Aberturas"
                value={inteiro.format(totalDeAberturas)}
                subLabel={filtros.interno ? "links do LinkFlow e uso interno" : "links do LinkFlow, sem o uso interno"}
                accent="blue"
              />
              <KpiCard label="Recursos por abertura" value={umaCasa.format(recursosPorAbertura)} subLabel="média de recursos diferentes usados" accent="emerald" />
              <KpiCard
                label="Só olharam"
                value={pct(Math.max(0, 1 - usaramAlgo / totalDeAberturas))}
                subLabel="aberturas sem nenhum recurso (zoom pela roda não conta)"
                accent="cyan"
              />
              <KpiCard
                label="Cliques em recurso travado"
                value={inteiro.format(travadoTotal)}
                subLabel={maisTravado ? `mais clicado: ${maisTravado.def.label}` : "nenhum no filtro"}
                accent="amber"
              />
            </div>

            <div className="flex shrink-0 flex-wrap items-end justify-between gap-2 border-b border-ink-800">
              <div className="flex gap-1 overflow-x-auto" role="tablist">
                {(
                  [
                    ["ranking", "Ranking"],
                    ["dia", "Por dia"],
                    ["org", "Por organização"],
                    ["barra", "Na barra"],
                  ] as const
                ).map(([valor, rotulo]) => (
                  <button
                    key={valor}
                    type="button"
                    role="tab"
                    aria-selected={aba === valor}
                    onClick={() => setAba(valor)}
                    className={`-mb-px whitespace-nowrap border-b-2 px-3 py-1.5 text-xs font-medium ${aba === valor ? "border-ink-200 text-ink-100" : "border-transparent text-ink-400 hover:text-ink-200"}`}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
              {aba !== "ranking" ? (
                <span className="mb-1.5">
                  <Segmentado
                    valor={medida}
                    opcoes={[
                      ["pct", "% das aberturas"],
                      ["cliques", "Cliques"],
                    ]}
                    onChange={(v) => setMedida(v as Medida)}
                  />
                </span>
              ) : null}
            </div>

            {aba === "ranking" ? <Ranking panel={painel} rows={linhas} onOpen={setAberto} /> : null}
            {aba === "dia" ? <PorDia panel={painel} rows={linhas} medida={medida} onOpen={setAberto} /> : null}
            {aba === "org" ? <PorOrganizacao panel={painel} rows={linhas} medida={medida} selecionada={filtros.org} onOpen={setAberto} /> : null}
            {aba === "barra" ? <NaBarra panel={painel} rows={linhas} medida={medida} interno={filtros.interno} onOpen={setAberto} /> : null}

            <p className="text-[11px] text-ink-500">
              «% das aberturas» = aberturas que usaram o recurso ao menos uma vez ÷ aberturas de viewer (ou de comparação, para os recursos da comparação) no filtro. Conta número, não conteúdo: nada do que foi lido ou medido fica guardado.
              {filtros.interno ? "" : " Fora o uso interno: gerenciador, links exportados e e-mails @linkflow.com.br."}
            </p>
          </>
        ) : null}
      </div>

      {painel && linhaAberta ? <Detalhe panel={painel} row={linhaAberta} onClose={() => setAberto(null)} /> : null}
    </section>
  );
}
